import { createContext, useEffect, useState, useContext } from 'react';

export const ApiContext = createContext();

let globalApiContext = null;
let apiLoadPromise = null;

export const getEndpoints = async () => {
  if (globalApiContext && !globalApiContext.isLoading) {
    return globalApiContext.endpoints;
  }
  
  if (apiLoadPromise) {
    return apiLoadPromise;
  }
  
  apiLoadPromise = fetch("/api.json")
    .then(response => response.json())
    .catch(error => {
      return {}; 
    });
  
  return apiLoadPromise;
};

export function buildEndpoint(key, ...segments) {
  if (!globalApiContext?.endpoints || Object.keys(globalApiContext.endpoints).length === 0) {
    return getEndpoints().then(endpoints => {
      const root = endpoints[key];
      if (!root) {
        throw new Error(`Unknown endpoint key: ${key}`);
      }

      if (!segments.length) return root;

      const tail = segments
        .filter(Boolean)
        .map(s => encodeURIComponent(String(s)))
        .join('/');

      return `${root.replace(/\/$/, '')}/${tail}`;
    });
  }

  const root = globalApiContext.endpoints[key];
  if (!root) {
    throw new Error(`Unknown endpoint key: ${key}`);
  }

  if (!segments.length) return root;

  const tail = segments
    .filter(Boolean)
    .map(s => encodeURIComponent(String(s)))
    .join('/');

  return `${root.replace(/\/$/, '')}/${tail}`;
}

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi debe ser usado dentro de un ApiProvider');
  }
  return context;
};

const ApiProvider = ({ children }) => {
  const [endpoints, setEndpoints] = useState({});
  
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (globalApiContext && !globalApiContext.isLoading) {
      setEndpoints(globalApiContext.endpoints);
      setIsLoading(false);
      return;
    }
    
    if (apiLoadPromise) {
      apiLoadPromise.then(data => {
        setEndpoints(prevEndpoints => ({...prevEndpoints, ...data}));
        setIsLoading(false);
      });
      return;
    }
    
    const loadEndpoints = async () => {
      try {
        const data = await getEndpoints();

        setEndpoints(prevEndpoints => ({...prevEndpoints, ...data}));
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadEndpoints();
  }, []);

  const contextValue = { endpoints, isLoading };
  globalApiContext = contextValue;

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
};

export default ApiProvider;